import { PrelineColourVariant } from '@payaca/types/preline';
import { getReadableTextColour } from '@payaca/utilities/colourUtilities';
import { clstx } from '@payaca/utilities/cvaMerge';
import { motion } from 'motion/react';
import { forwardRef, PropsWithChildren, useMemo } from 'react';

export type BadgeProps = {
  className?: string;
  rounded?: boolean;
  size?: 'sm' | 'md';
  colour?: PrelineColourVariant;
  customColour?: string;
  label?: string;
  variant: 'solid' | 'soft' | 'outline' | 'custom' | 'white';
};

const badgeVariantStyles = (args: Pick<BadgeProps, 'variant' | 'colour'>) => {
  const { variant, colour = 'gray' } = args;

  if (variant === 'white') {
    return 'border border-gray-200 bg-white text-gray-800 shadow-sm';
  }

  if (colour === 'job-brand') {
    switch (variant) {
      case 'solid':
        return `bg-job-brand text-job-brand-readable`;
      case 'soft':
        return `bg-job-brand-light text-job-brand-readable`;
      case 'outline':
        return `border border-job-brand text-job-brand`;
    }
  }

  if (colour === 'job-brand-white') {
    return `bg-white text-job-brand`;
  }

  switch (variant) {
    case 'solid':
      switch (colour) {
        case 'black':
          return `bg-gray-800 text-white`;
        case 'gray':
          return `bg-gray-500 text-white`;
        case 'green':
          return `bg-green-500 text-white`;
        case 'blue':
          return `bg-blue-600 text-white`;
        case 'red':
          return `bg-red-500 text-white`;
        case 'yellow':
          return `bg-yellow-500 text-white`;
        case 'white':
          return 'bg-white text-gray-600';
        case 'purple':
          return 'bg-purple-500 text-white';
        default:
          return '';
      }
    case 'soft':
      switch (colour) {
        case 'black':
          return `bg-gray-100 text-gray-800`;
        case 'gray':
          return `bg-gray-50 text-gray-800`;
        case 'green':
          return `bg-green-100 text-green-800`;
        case 'blue':
          return `bg-blue-100 text-blue-800`;
        case 'red':
          return `bg-red-100 text-red-800`;
        case 'yellow':
          return `bg-yellow-100 text-yellow-800`;
        case 'white':
          return 'bg-white/10 text-white';
        case 'purple':
          return 'bg-purple-500 text-white';
        case 'orange':
          return 'bg-orange-100 text-orange-800';
        default:
          return '';
      }
    case 'outline':
      switch (colour) {
        case 'black':
          return `border border-gray-800 text-gray-800`;
        case 'gray':
          return `border border-gray-500 text-gray-500`;
        case 'green':
          return `border border-green-500 text-green-500`;
        case 'blue':
          return `border border-blue-500 text-blue-600`;
        case 'red':
          return `border border-red-500 text-red-500`;
        case 'yellow':
          return `border border-yellow-500 text-yellow-500`;
        case 'white':
          return 'border border-white text-white';
        case 'purple':
          return 'border border-purple-500 text-white';
        default:
          return ``;
      }
    case 'custom':
      return '';
  }
};

/**
 * A small label that displays a persisted state on a record.
 *
 * ## When to use
 *
 * Only use a Badge for state that is stored on the entity. Examples:
 *
 * - Proposal status (draft, sent, accepted)
 * - Project pipeline stage
 * - User role
 * - Tags on a record
 *
 * Test: if you deleted the Badge, the state would still exist in the database.
 * If the "state" only exists in the UI, a Badge is wrong.
 *
 * ## When to choose another component
 *
 * - **Emphasising ordinary text** — use **bold** text, not a Badge.
 * - **Messages or callouts** — use `Alert`.
 * - **A long list of values** — use `EclipseBadge`. It shows the first value
 *   plus a "+N" Badge with a Tooltip.
 * - **To represent a user** — use `UserBadge`.
 * - **Anything clickable** — a Badge is a static `span`. Use a `Button` or
 *   `AnchorButton` instead.
 *
 * ## Content rules
 *
 * - Use colour to carry meaning consistently: `green` = positive, `red` =
 *   negative or blocked, `yellow` = pending or warning, `gray` = neutral or
 *   inactive.
 *
 * ## Accessibility
 *
 * Use the `label` prop to add screen-reader-only context when the visible
 * content alone is ambiguous.
 */
const Badge = forwardRef<HTMLSpanElement, PropsWithChildren<BadgeProps>>(
  (props, ref) => {
    const {
      className,
      rounded = false,
      size = 'md',
      variant,
      colour,
      customColour,
      label,
      children,
    } = props;

    const readableTextColour = useMemo(() => {
      if (variant === 'custom') {
        if (!customColour) {
          console.error(
            "Badge: 'customColour' is required when variant is 'custom'"
          );
        }

        return getReadableTextColour(customColour || 'black');
      }
    }, [variant, customColour]);

    return (
      <span
        ref={ref}
        className={clstx(
          'inline-flex items-center text-sm font-medium',
          rounded ? 'rounded-lg' : 'rounded-full',
          size === 'sm' && 'py-0.5 px-2 gap-1',
          size === 'md' && 'py-1.5 px-3 gap-1.5',
          badgeVariantStyles({ variant, colour }),
          className
        )}
        style={
          variant === 'custom'
            ? { backgroundColor: customColour, color: readableTextColour }
            : {}
        }
      >
        {label && <span className="sr-only">{label}</span>}
        {children}
      </span>
    );
  }
);

export default Object.assign(Badge, {
  motion: motion.create(Badge),
});
